import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerFormWithNote } from '@/components/note-form';
import { PERMISSION_OBJ_CAMPAIGN } from '@/constants/permissions';
import { campaignStore } from '@/stores';
import { NoteObjectType } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';

import { getDefaultCampaignCreateValues } from './default-create-values';
import { getFormFields } from './setup';

const entity = 'campaigns';
const store = campaignStore;

export const CampaignForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { campaignId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +campaignId);

  // Deep-link entry skips the table page that normally warms this cache; without
  // it `getDefaultCampaignCreateValues` falls back to today-based logic and can
  // produce a collision with rows already in DB.
  const { loading: storeLoading } = useMassUpdateCashedStores([store]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const initValues = !isCreate && foundItem ? { ...foundItem } : isCreate ? getDefaultCampaignCreateValues() : {};

  const title = isCreate
    ? getTranslatedString('campaigns.create-new')
    : getTranslatedString('campaigns.campaign-number', { 0: campaignId });

  const formFields = getFormFields();
  const allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_CAMPAIGN);

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => api[entity].create(values).source.then(({ data }: any) => store.addItem(data)),
    update: (values: any) =>
      api[entity].update(foundItem.id, values).source.then(({ data }: any) => store.updateItem(data)),
  });

  return (
    <DrawerFormWithNote
      objectType={NoteObjectType.Campaign}
      resourceId={campaignId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={(isCreate && storeLoading) || (!isCreate && !foundItem)}
      width={720}
    />
  );
});
